"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "No subscription gate",
    rule: "Unlike Division, Branch, and Warehouse, creating a Process isn't limited by your plan",
  },
  {
    topic: "Unique name",
    rule: "Process names must be unique per company, checked case-insensitively",
  },
  {
    topic: "No active/inactive status",
    rule: "There's no deactivate action on a Process, only Edit and Delete",
  },
  {
    topic: "Deleting",
    rule: "Blocked only if a Recipe still includes this Process. A Production Order that already ran the process doesn't block it, because the order keeps its own copy of the step",
  },
  {
    topic: "No per-step data lives here",
    rule: "Input quantity, output quantity, scrap, manpower, and timing are all entered fresh on each Production Order, never stored on the Process itself",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. A Process is just a named step, nothing more" },
      { heading: "2. It only ever gets used inside a Recipe" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function ProcessPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Process
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone naming the manufacturing steps used to build a Recipe
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <span className="geist-medium text-[#111827]">Process</span> is
            deliberately small, a name and nothing else. It exists so a{" "}
            <span className="geist-medium text-[#111827]">Recipe</span> has
            something to string together in order, not to hold any detail
            about how that step is actually carried out.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Just a label */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. A Process is just a named step, nothing more
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The Add Process form has exactly two fields, and that&apos;s the
            whole record.
          </p>
          <Definitions
            items={[
              {
                term: "Process Name",
                desc: "Must be unique across your company.",
              },
              {
                term: "Process Code",
                desc: "Assigned automatically with a PROC prefix.",
              },
              {
                term: "Duration, cost, instructions, sequence",
                desc: "None of these exist on a Process. It's purely a label you'll reuse across Recipes.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Add Process form, showing just a code and a name." />

          {/* 2. Used inside Recipe */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. It only ever gets used inside a Recipe
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            A Process is never selected directly on a Production Order. It
            reaches production only through the Recipe it&apos;s part of.
          </p>
          <Definitions
            items={[
              {
                term: "Inside a Recipe",
                desc: "You pick one or more Processes and put them in order. That order is what defines the production sequence.",
              },
              {
                term: "When production actually runs",
                desc: "The system copies just the name and code of each process onto the Production Order. It doesn't stay linked back to this master.",
              },
              {
                term: "What actually gets tracked per run",
                desc: "Quantity in, quantity out, scrap, manpower, and timing are all entered on the Production Order itself, step by step, as the work happens.",
              },
            ]}
          />
          <Callout type="note">
            Because Production Orders keep their own copy of each step,
            editing or deleting a Process later has no effect on runs that
            already used it. It only changes new Recipes you build with it
            from that point on.
          </Callout>
          <Screenshot caption="Screenshot: the Processes field on Add Recipe, showing steps selected in order." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
          <Link
            href="/documentation/books/all-master/recipe"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Recipe <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Strings Processes together into a production sequence
            </p>
          </Link>
          <Link
            href="/documentation/books/production/add-production"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Add Production <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where each step's real quantities and timing are actually recorded
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
